import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { oli_products_get, oli_create_update_project, oli_product_users_get_with_project_data } from "../services/apiService";
import ObjectForm from "@/components/form/ObjectForm";
import { useAuth } from '../hooks/AuthContext';
import LogoutButton from '../components/LogoutButton';
import xoroLogo from "../assects/img/X_logo.png";

function OliConfigurator() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [projectData, setProjectData] = useState([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [selectedProjectName, setSelectedProjectName] = useState<string | null>(null);
  const [selectedProjectInfo, setSelectedProjectInfo] = useState<string | null>(null);
  const [newProject, setNewProject] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'vps' | 'domains' | 'icps'>('details');
  const [allFieldsFilled, setAllFieldsFilled] = useState(false);
  const { logout, oliLogout } = useAuth();

  const itemsPerPage = 10;

  const [newData, setNewData] = useState({
    projectName: "",
    projectInfo: "",
  });

  const [vps, setVps] = useState<Record<number, string>>({ 1: "" });
  const [domains, setDomains] = useState<Record<number, string>>({ 1: "" });
  const [icps, setIcps] = useState<Record<number, string>>({ 1: "" });

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewData({ ...newData, [name]: value });
  };

  const filteredProjects = projectData.filter(
    (product: any) =>
      product.project_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
  const paginatedProjects = filteredProjects.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleRowClick = async (id: any, project_name: string, project_info: string) => {
    setSelectedProjectId(id);
    setSelectedProjectName(project_name);
    setSelectedProjectInfo(project_info);
    setShowModal(true);
    setNewProject(false);
    const response = await oli_product_users_get_with_project_data(Number(id));

    setNewData({
      projectName: project_name,
      projectInfo: project_info,
    });

    const set_vps: any = {};
    for (let i = 0; i < response?.vps?.length; i++) {
      set_vps[i + 1] = response.vps[i].value_proposition;
    }
    setVps(set_vps);

    const set_domains: any = {};
    for (let i = 0; i < response?.domains?.length; i++) {
      set_domains[i + 1] = response.domains[i].domain;
    }
    setDomains(set_domains);

    const set_icps: any = {};
    for (let i = 0; i < response?.icps?.length; i++) {
      set_icps[i + 1] = response.icps[i].icp;
    }
    setIcps(set_icps);
  };

  async function getAll() {
    setLoading(true);
    const response = await oli_products_get();
    if (response.status != "No users found") {
      setProjectData(response);
    }
    setLoading(false);
  }

  useEffect(() => {
    getAll();
  }, []);

  useEffect(() => {
    const allFilled = newData.projectName && newData.projectInfo && Object.values(vps).every((vp) => vp.trim() !== "") && Object.values(domains).every((domain) => domain.trim() !== "") && Object.values(icps).every((icp) => icp.trim() !== "");
    setAllFieldsFilled(Boolean(allFilled));
  }, [newData, vps, domains, icps]);

  const handleFormSubmit = async () => {
    setLoading(true)
    if (!newData.projectName || !newData.projectInfo) {
      setLoading(false)
      alert("Please fill all fields.");
      return;
    }

    if (Object.values(vps).some((vp) => vp.trim() === "")) {
      alert("Please fill all value proposition.");
      setLoading(false);
      return;
    }

    if (Object.values(domains).some((domain) => domain.trim() === "")) {
      alert("Please fill all domains.");
      setLoading(false);
      return;
    }

    if (Object.values(icps).some((icp) => icp.trim() === "")) {
      alert("Please fill all ICPs.");
      setLoading(false);
      return;
    }

    if (Object.values(vps).some((vp) => vp.trim() === "")) {
      alert("Please fill all value proposition.");
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("projectName", newData.projectName);
    formData.append("projectInfo", newData.projectInfo);
    const vpsArray = Object.values(vps)
    const domainsArray = Object.values(domains)
    const icpsArray = Object.values(icps)

    formData.append("vps", JSON.stringify(vpsArray));
    formData.append("domains", JSON.stringify(domainsArray));
    formData.append("icps", JSON.stringify(icpsArray));

    if (!newProject) {
      formData.append("newProject", "false");
      formData.append("projectId", String(selectedProjectId));
    }
    else {
      formData.append("newProject", "true");
    }

    try {
      await oli_create_update_project(formData);
      setShowModal(false);
      setNewData({ projectName: "", projectInfo: "" });
      setVps({ 1: "" });
      setDomains({ 1: "" });
      setIcps({ 1: "" });
      setSelectedProjectId(null);
      setSelectedProjectName(null);
      setSelectedProjectInfo(null);
      setNewProject(false);
      await getAll();
      alert("Upload successful");
    } catch (error) {
      console.error("Upload failed", error);
      alert("Upload failed.");
    }
    finally {
      setLoading(false)
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      {/* Professional Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200/60 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">

            {/* Brand & Title */}
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                {/*<div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-sm ring-1 ring-blue-600/20 p-1">
      <img 
        src="/assets/img/X_logo.png" 
        alt="X_Logo" 
        className="w-6 h-6 object-contain"
      />
    </div>*/}
                <div className="p-2.5 bg-white rounded-xl shadow-lg border border-gray-200/50">
                  <img
                    src={xoroLogo}
                    alt="Avironix Logo"
                    className="w-6 h-6 object-cover rounded-full"
                  />
                </div>
              </div>
              <div>
                <h1 className="text-xl font-semibold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  OLI
                </h1>
                <p className="text-sm text-gray-500 font-medium">Powered by XORO Story</p>
              </div>
            </div>

            {/* User Info & Actions */}
            <div className="flex items-center space-x-4">
              {/* <div className="w-8 h-8 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center ring-2 ring-white">
          <span className="text-xs font-semibold text-gray-700">
            {localStorage.getItem('oli_username')?.charAt(0)?.toUpperCase() || 'U'}
          </span>
        </div> */}
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">
                  {localStorage.getItem('oli_username')}
                </p>
                <p className="text-xs text-blue-600 font-medium">Administrator</p>
              </div>

              <LogoutButton />
            </div>
          </div>
        </div>
      </header>
      {/*Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/*Page Header with Actions */}
        <div className="mb-8">
          <div className="sm:flex sm:items-center sm:justify-between">
            <div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-700 bg-clip-text text-transparent">
                Configurator
              </h2>
              {/*  <p className="mt-2 text-base text-gray-600 leading-relaxed">
          Configure product details, value propositions, domains, and ICPs
        </p>*/}
              <div className="mt-3 flex items-center space-x-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-500">
                  {projectData.length} {projectData.length === 1 ? 'project' : 'projects'} configured
                </span>
              </div>
            </div>
            <div className="mt-6 sm:mt-0 flex space-x-3">
              <button
                onClick={() => { window.location.href = '/OliProducts'; }}
                className="inline-flex items-center px-4 py-2 border border-gray-300/60 text-sm font-medium rounded-xl text-gray-700 bg-white/80 backdrop-blur-sm hover:bg-white hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                View Analytics
              </button>
              <button
                onClick={() => {
                  setShowModal(true);
                  setNewProject(true);
                  setSelectedProjectId(null);
                  setSelectedProjectName(null);
                  setNewData({ projectName: "", projectInfo: "" });
                  setVps({ 1: "" });
                  setDomains({ 1: "" });
                  setIcps({ 1: "" });
                }}
                className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-semibold rounded-xl text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Project
              </button>
            </div>
          </div>
          {/*  Search Bar */}
          <div className="mt-8">
            <div className="max-w-md">
              <label htmlFor="search" className="sr-only">Search projects</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  id="search"
                  placeholder="Search by project name..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl leading-5 bg-white/80 backdrop-blur-sm placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                />
              </div>
            </div>
          </div>
        </div>
        {/*Projects Table Card */}
        <div className="bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl border border-gray-200/60 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200/60">
              <thead className="bg-gradient-to-r from-gray-50/80 to-gray-100/50 backdrop-blur-sm">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">S.No</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Product Name</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Product Info</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white/60 backdrop-blur-sm divide-y divide-gray-200/40">
                {paginatedProjects.length !== 0 ? paginatedProjects.map((project: any, index: number) => (
                  <tr
                    key={project.id}
                    className="hover:bg-blue-50/50 transition-colors duration-200 group"
                  >
                    <td className="px-6 py-6 whitespace-nowrap text-sm font-medium text-gray-900">
                      <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 text-blue-700 font-bold group-hover:from-blue-200 group-hover:to-blue-300 transition-all duration-200">
                        {index + 1 + (currentPage - 1) * itemsPerPage}
                      </span>
                    </td>
                    <td className="px-6 py-6 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full mr-4 animate-pulse"></div>
                        <div>
                          <div className="text-sm font-bold text-gray-900 group-hover:text-blue-700 transition-colors duration-200">
                            {project.project_name}
                          </div>
                          <div className="w-0 group-hover:w-16 h-0.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-300 mt-1"></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-6">
                      <div className="text-sm text-gray-900 max-w-md">
                        <div className="line-clamp-2 leading-relaxed group-hover:text-gray-700 transition-colors duration-200">
                          {project.product_details}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-6 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleRowClick(project.id, project.project_name, project.product_details)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-xs font-semibold rounded-xl text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                      >
                        <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Configure
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={4} className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center">
                        <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mb-4">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">No projects found</h3>
                        <p className="text-sm text-gray-500">Get started by creating your first project configuration.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {/* Pagination */}
        <div className="mt-6 flex items-center justify-between bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-gray-200/60">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
            className="inline-flex items-center px-4 py-2 border border-gray-300/60 text-sm font-medium rounded-lg text-gray-700 bg-white/80 hover:bg-white hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 backdrop-blur-sm"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>

          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700 font-medium">
              Page <span className="font-bold text-blue-600">{currentPage}</span> of{" "}
              <span className="font-bold text-blue-600">{totalPages}</span>
            </span>
          </div>

          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
            className="inline-flex items-center px-4 py-2 border border-gray-300/60 text-sm font-medium rounded-lg text-gray-700 bg-white/80 hover:bg-white hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 backdrop-blur-sm"
          >
            Next
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </main>
      {/*  Configuration Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity"
              onClick={() => setShowModal(false)}
            />

            {/*  Modal panel */}
            <div className="relative inline-block align-bottom bg-white/95 backdrop-blur-sm rounded-2xl text-left overflow-hidden shadow-2xl border border-gray-200/60 transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
              {/*Header */}
              <div className="bg-gradient-to-r from-white/90 to-blue-50/50 px-6 pt-6 pb-4 border-b border-gray-200/60">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                      {newProject ? "Create New Project" : `Configure: ${selectedProjectName}`}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">Configure project details and settings</p>
                  </div>
                  <button
                    onClick={() => setShowModal(false)}
                    className="rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 transition-all duration-200"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Tab Navigation */}
                <div className="mt-6">
                  <div className="border-b border-gray-200/60">
                    <nav className="-mb-px flex space-x-8">
                      {[
                        { key: 'details', label: 'Project Details', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
                        { key: 'vps', label: 'Value Props', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
                        { key: 'domains', label: 'Domains', icon: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064' },
                        { key: 'icps', label: 'ICPs', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z' }
                      ].map((tab, index) => (
                        <button
                          key={tab.key}
                          onClick={() => setActiveTab(tab.key as any)}
                          className={[
                            "group relative inline-flex items-center py-3 px-1 border-b-2 font-semibold text-sm transition-all duration-200",
                            activeTab === tab.key
                              ? "border-blue-500 text-blue-600"
                              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                          ].join(" ")}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-2 transition-all duration-200 ${activeTab === tab.key
                              ? 'bg-blue-100 text-blue-600'
                              : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                            }`}>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                            </svg>
                          </div>
                          {tab.label}
                          {activeTab === tab.key && (
                            <div className="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"></div>
                          )}
                        </button>
                      ))}
                    </nav>
                  </div>
                </div>
              </div>

              {/* Tab Content */}
              <div className="px-6 py-6 max-h-96 overflow-y-auto">
                {activeTab === 'details' && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Project Name</label>
                      <input
                        type="text"
                        name="projectName"
                        value={newData.projectName}
                        onChange={handleFormChange}
                        className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white/80 backdrop-blur-sm sm:text-sm transition-all duration-200 h-12 px-4"
                        placeholder="Enter project name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Project Information</label>
                      <textarea
                        name="projectInfo"
                        value={newData.projectInfo}
                        onChange={(e: any) => handleFormChange(e as React.ChangeEvent<any>)}
                        rows={4}
                        className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white/80 backdrop-blur-sm sm:text-sm transition-all duration-200 resize-none p-4"
                        placeholder="Describe your project..."
                      />
                    </div>
                  </div>
                )}


                {activeTab === 'vps' && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h4 className="text-lg font-semibold text-gray-900">Value Propositions</h4>
                      <button
                        onClick={() => setVps({ ...vps, [Object.keys(vps).length + 1]: "" })}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-xs font-semibold rounded-xl text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                      >
                        <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Value Proposition
                      </button>
                    </div>
                    <div className="space-y-4">
                      {Object.keys(vps).map((key: any, index) => (
                        <div key={index} className="bg-gradient-to-r from-gray-50/80 to-blue-50/30 backdrop-blur-sm rounded-xl p-4 border border-gray-200/60">
                          <div className="flex items-center space-x-4">
                            <div className="flex-1">
                              <label className="block text-xs font-semibold text-gray-600 mb-2">
                                Value Proposition {index + 1}
                              </label>
                              <input
                                type="text"
                                value={vps[key] || ""}
                                onChange={(e) => setVps((prev: any) => ({ ...prev, [key]: e.target.value }))}
                                className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white/80 backdrop-blur-sm sm:text-sm transition-all duration-200 h-12 px-4"
                                placeholder="Enter value proposition"
                              />
                            </div>
                            {Object.keys(vps).length > 1 && (
                              <button
                                onClick={() => {
                                  const updated = { ...vps }; delete updated[key]; setVps(updated);
                                }}
                                className="mt-6 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'domains' && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h4 className="text-lg font-semibold text-gray-900">Domains</h4>
                      <button
                        onClick={() => setDomains({ ...domains, [Object.keys(domains).length + 1]: "" })}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-xs font-semibold rounded-xl text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                      >
                        <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Domain
                      </button>
                    </div>
                    <div className="space-y-4">
                      {Object.keys(domains).map((key: any, index) => (
                        <div key={index} className="bg-gradient-to-r from-gray-50/80 to-blue-50/30 backdrop-blur-sm rounded-xl p-4 border border-gray-200/60">
                          <div className="flex items-center space-x-4">
                            <div className="flex-1">
                              <label className="block text-xs font-semibold text-gray-600 mb-2">
                                Domain {index + 1}
                              </label>
                              <input
                                type="text"
                                value={domains[key] || ""}
                                onChange={(e) => setDomains((prev: any) => ({ ...prev, [key]: e.target.value }))}
                                className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white/80 backdrop-blur-sm sm:text-sm transition-all duration-200 h-12 px-4"
                                placeholder="Enter domain"
                              />
                            </div>
                            {Object.keys(domains).length > 1 && (
                              <button
                                onClick={() => {
                                  const updated = { ...domains }; delete updated[key]; setDomains(updated);
                                }}
                                className="mt-6 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {activeTab === 'icps' && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h4 className="text-lg font-semibold text-gray-900">Ideal Customer Profiles</h4>
                      <button
                        onClick={() => setIcps({ ...icps, [Object.keys(icps).length + 1]: "" })}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-xs font-semibold rounded-xl text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                      >
                        <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add ICP
                      </button>
                    </div>
                    <div className="space-y-4">
                      {Object.keys(icps).map((key: any, index) => (
                        <div key={index} className="bg-gradient-to-r from-gray-50/80 to-blue-50/30 backdrop-blur-sm rounded-xl p-4 border border-gray-200/60">
                          <div className="flex items-center space-x-4">
                            <div className="flex-1">
                              <label className="block text-xs font-semibold text-gray-600 mb-2">
                                ICP {index + 1}
                              </label>
                              <input
                                type="text"
                                value={icps[key] || ""}
                                onChange={(e) => setIcps((prev: any) => ({ ...prev, [key]: e.target.value }))}
                                className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white/80 backdrop-blur-sm sm:text-sm transition-all duration-200 h-12 px-4"
                                placeholder="Enter ICP"
                              />
                            </div>
                            {Object.keys(icps).length > 1 && (
                              <button
                                onClick={() => {
                                  const updated = { ...icps }; delete updated[key]; setIcps(updated);
                                }}
                                className="mt-6 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/*  Footer */}
              <div className="bg-gradient-to-r from-gray-50/80 to-blue-50/30 px-6 py-5 flex items-center justify-between border-t border-gray-200/60">
                <div className="flex items-center space-x-4">
                  {activeTab !== 'details' && (
                    <button
                      onClick={() => {
                        if (activeTab === 'vps') {
                          setActiveTab("details" as any)
                        }
                        else if (activeTab === 'domains') {
                          setActiveTab("vps" as any)
                        }
                        else if (activeTab === 'icps') {
                          setActiveTab("domains" as any)
                        }
                      }}
                      className="inline-flex items-center px-4 py-2.5 border border-gray-300/60 text-sm font-medium rounded-xl text-gray-700 bg-white/80 hover:bg-white hover:shadow-md transition-all duration-200 backdrop-blur-sm"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Previous
                    </button>
                  )}

                  {/* Progress Indicator */}
                  <div className="flex items-center space-x-2">
                    <div className="text-xs font-medium text-gray-500">Step {
                      activeTab === 'details' ? '1' :
                        activeTab === 'vps' ? '2' :
                          activeTab === 'domains' ? '3' : '4'
                    } of 4</div>
                    <div className="flex items-center space-x-1">
                      {['details', 'vps', 'domains', 'icps'].map((tab, i) => (
                        <div key={tab} className={`w-2 h-2 rounded-full transition-all duration-200 ${activeTab === tab ? 'bg-blue-600 w-6' :
                            ['details', 'vps', 'domains', 'icps'].indexOf(activeTab) > i ? 'bg-emerald-500' : 'bg-gray-300'
                          }`} />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setShowModal(false)}
                    className="inline-flex items-center px-5 py-2.5 border border-gray-300/60 text-sm font-medium rounded-xl text-gray-700 bg-white/80 hover:bg-white hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 backdrop-blur-sm"
                  >
                    Cancel
                  </button>

                  {activeTab === "icps" ? (
                    <button
                      onClick={handleFormSubmit}
                      className={[
                        "inline-flex items-center px-6 py-2.5 border border-transparent text-sm font-semibold rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5",
                        allFieldsFilled
                          ? "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                          : "bg-gray-400 cursor-not-allowed"
                      ].join(" ")}
                      disabled={!allFieldsFilled}
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {newProject ? "Create Project" : "Update Project"}
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        if (activeTab === 'details') {
                          setActiveTab("vps" as any)
                        }
                        else if (activeTab === 'vps') {
                          setActiveTab("domains" as any)
                        }
                        else if (activeTab === 'domains') {
                          setActiveTab("icps" as any)
                        }
                      }}
                      className="inline-flex items-center px-6 py-2.5 border border-transparent text-sm font-semibold rounded-xl text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    >
                      Continue
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OliConfigurator;